import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import {
  CURVE_TYPES,
  nextAvailableCurveName,
  type Curve,
  type CurvePoint,
} from "../model/inp/curves.ts";
import { parseClipboardGrid } from "../model/clipboardGrid.ts";
import { handleGridArrowNav, handleGridDeleteRow } from "./gridKeyboardNav.ts";
import { NumberInput } from "./fields.tsx";
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_SERIES_PRIMARY,
  CHART_TOOLTIP_STYLE,
} from "./chartTheme.ts";
import { useT } from "../i18n/hooks.ts";
import { optionLabelKey } from "../i18n/fieldLabels.ts";
import { PlusIcon } from "./PlusIcon.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";

/** Buffers X/Y locally so typing a digit doesn't copy the whole `points` array on every keystroke — only on blur, same pattern as TimeSeriesEditor's own `TimeSeriesPointRow`. */
function CurvePointRow({
  point,
  onCommit,
  onPaste,
  onKeyDown,
}: {
  point: CurvePoint;
  onCommit: (patch: Partial<CurvePoint>) => void;
  onPaste: (e: ClipboardEvent<HTMLInputElement>, col: 0 | 1) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const [x, setX] = useState(point.x);
  const [y, setY] = useState(point.y);
  useEffect(() => setX(point.x), [point.x]);
  useEffect(() => setY(point.y), [point.y]);
  return (
    <tr>
      <td>
        <NumberInput
          value={x}
          onChange={setX}
          onBlur={() => onCommit({ x })}
          onPaste={(e) => onPaste(e, 0)}
          onKeyDown={onKeyDown}
        />
      </td>
      <td>
        <NumberInput
          value={y}
          onChange={setY}
          onBlur={() => onCommit({ y })}
          onPaste={(e) => onPaste(e, 1)}
          onKeyDown={onKeyDown}
        />
      </td>
    </tr>
  );
}

/**
 * The [CURVES] tab of ProjectDataDialog.tsx — curve list on the left, the
 * selected one's Type + point table + live line chart on the right.
 * `curves`/`onChange` is the dialog's own draft array; nothing here calls
 * mutateRaw directly. Delete/Supr removes the row the focused cell is in
 * (no per-row trash icon), per user request.
 */
export function CurvesEditor({
  curves,
  onChange,
  selectedName,
  onSelect,
}: {
  curves: Curve[];
  onChange: (curves: Curve[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deleteCurve,
    rename: renameSelected,
    update: updateSelected,
  } = useNamedCollection(curves, onChange, selectedName, onSelect);

  // Stable per-row key, same reasoning (and same lockstep-with-mutations
  // approach) as TimeSeriesEditor.tsx's own `pointIdsRef` — see its header
  // comment for why an id embedded IN a `CurvePoint` wouldn't actually be
  // more stable than the index itself in this codebase.
  const pointIdsRef = useRef<string[]>([]);
  const nextPointIdRef = useRef(0);
  if (pointIdsRef.current.length !== (selected?.points.length ?? 0)) {
    pointIdsRef.current = (selected?.points ?? []).map(() => `p${nextPointIdRef.current++}`);
  }

  function addCurve() {
    const name = nextAvailableCurveName(new Set(curves.map((c) => c.name)));
    add({ name, type: CURVE_TYPES[0], points: [{ x: 0, y: 0 }] });
  }

  /** Never removes the last remaining point — chart/table both assume at least one row exists. */
  function removePoint(index: number) {
    updateSelected((c) => {
      if (c.points.length <= 1) return c;
      pointIdsRef.current = pointIdsRef.current.filter((_, j) => j !== index);
      return { ...c, points: c.points.filter((_, j) => j !== index) };
    });
  }

  /** `colIndex` 0 = X, 1 = Y — a row past the last existing point is appended rather than dropped. */
  function handlePointsPaste(
    e: ClipboardEvent<HTMLInputElement>,
    rowIndex: number,
    colIndex: 0 | 1
  ) {
    e.preventDefault();
    const grid = parseClipboardGrid(e.clipboardData.getData("text"));
    updateSelected((c) => {
      const points: CurvePoint[] = [...c.points];
      const ids = [...pointIdsRef.current];
      grid.forEach((row, r) => {
        const targetRow = rowIndex + r;
        while (points.length <= targetRow) {
          points.push({ x: 0, y: 0 });
          ids.push(`p${nextPointIdRef.current++}`);
        }
        row.forEach((cell, cOffset) => {
          const col = colIndex + cOffset;
          const value = Number(cell);
          if (col > 1 || Number.isNaN(value)) return;
          points[targetRow] =
            col === 0 ? { ...points[targetRow], x: value } : { ...points[targetRow], y: value };
        });
      });
      pointIdsRef.current = ids;
      return { ...c, points };
    });
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={curves}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addCurve}
        addLabel={t("projectData.addCurve")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("projectData.noCurveSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deleteCurve(selected.name)}
              deleteLabel={t("projectData.deleteCurve")}
            >
              <label className="field-pill">
                <span className="field-pill-label">{t("projectData.curveType")}</span>
                <select
                  value={selected.type}
                  onChange={(e) => updateSelected((c) => ({ ...c, type: e.target.value }))}
                >
                  {CURVE_TYPES.map((type) => {
                    const key = optionLabelKey(type);
                    return (
                      <option key={type} value={type}>
                        {key ? t(key) : type}
                      </option>
                    );
                  })}
                </select>
              </label>
            </NamedCollectionHeader>
            <div className="curve-editor-body">
              <div className="curve-editor-table">
                <span className="curve-editor-section-title">{t("projectData.valuesTable")}</span>
                <div className="named-collection-points-table-wrap">
                  <table className="named-collection-points-table">
                    <thead>
                      <tr>
                        <th>X</th>
                        <th>Y</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.points.map((point, i) => (
                        <CurvePointRow
                          key={pointIdsRef.current[i] ?? i}
                          point={point}
                          onCommit={(patch) =>
                            updateSelected((c) => ({
                              ...c,
                              points: c.points.map((p, j) => (j === i ? { ...p, ...patch } : p)),
                            }))
                          }
                          onPaste={(e, col) => handlePointsPaste(e, i, col)}
                          onKeyDown={(e) => {
                            handleGridArrowNav(e);
                            handleGridDeleteRow(e, () => removePoint(i));
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  className="button"
                  onClick={() =>
                    updateSelected((c) => {
                      pointIdsRef.current = [
                        ...pointIdsRef.current,
                        `p${nextPointIdRef.current++}`,
                      ];
                      const last = c.points[c.points.length - 1];
                      return { ...c, points: [...c.points, { x: (last?.x ?? 0) + 1, y: 0 }] };
                    })
                  }
                >
                  <PlusIcon />
                  {t("projectData.addPoint")}
                </button>
              </div>
              <div className="curve-editor-chart">
                <span className="curve-editor-section-title">{t("projectData.chartPreview")}</span>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={selected.points}
                    margin={{ top: 4, right: 12, bottom: 0, left: -12 }}
                  >
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis
                      dataKey="x"
                      type="number"
                      domain={["dataMin", "dataMax"]}
                      tick={CHART_AXIS_TICK}
                    />
                    <YAxis dataKey="y" tick={CHART_AXIS_TICK} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Line
                      type="linear"
                      dataKey="y"
                      stroke={CHART_SERIES_PRIMARY}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
